import {Directive, ElementRef, HostListener, Input, OnDestroy, Renderer2} from '@angular/core';

@Directive({
    selector: '[mmTooltip]',
    standalone: false
})
export class TooltipDirective implements OnDestroy {

    @Input('mmTooltip') public text: string | undefined;

    private tooltipElement: HTMLElement | undefined = undefined;

    constructor(private readonly host: ElementRef, private readonly renderer: Renderer2) {
    }

    @HostListener('mouseenter')
    public onMouseEnter(): void {
        this.show();
    }

    @HostListener('mouseleave')
    public onMouseLeave(): void {
        this.hide();
    }

    public ngOnDestroy(): void {
        this.hide();
    }

    private show(): void {
        if (this.tooltipElement !== undefined || this.text === undefined || this.text === '') {
            return;
        }

        const tooltip = this.renderer.createElement('div') as HTMLElement;
        this.renderer.appendChild(tooltip, this.renderer.createText(this.text));

        // Tooltip is appended to <body>, outside the app's font scope, so inherit
        // the hovered element's computed font instead of the serif browser default.
        const fontFamily = window.getComputedStyle(this.host.nativeElement as Element).fontFamily;

        this.applyStyles(tooltip, {
            'font-family': fontFamily,
            position: 'fixed',
            'z-index': '10000',
            'pointer-events': 'none',
            'background-color': 'rgba(50, 50, 50, 0.9)',
            color: 'white',
            padding: '4px 8px',
            'border-radius': '4px',
            'font-size': '12px',
            'line-height': '1.4',
            'text-align': 'center',
            'max-width': '250px',
            'white-space': 'pre-line',
            transform: 'translate(-50%, 0)'
        });

        this.renderer.appendChild(document.body, tooltip);
        this.tooltipElement = tooltip;
        this.position();
    }

    private position(): void {
        if (this.tooltipElement === undefined) {
            return;
        }
        const hostRect = (this.host.nativeElement as Element).getBoundingClientRect();
        const top = hostRect.bottom + 6;
        const left = hostRect.left + (hostRect.width / 2);
        this.renderer.setStyle(this.tooltipElement, 'top', `${top}px`);
        this.renderer.setStyle(this.tooltipElement, 'left', `${left}px`);
    }

    private hide(): void {
        if (this.tooltipElement !== undefined) {
            this.renderer.removeChild(document.body, this.tooltipElement);
            this.tooltipElement = undefined;
        }
    }

    private applyStyles(element: HTMLElement, styles: Record<string, string>): void {
        Object.entries(styles).forEach(([property, value]) => this.renderer.setStyle(element, property, value));
    }
}
